import { useBackButtonHijack } from "@hooks/useBackButtonHijack";
import { useI18nContext } from "@i18n/i18n-react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Button } from "@shadcn/ui/button";
import { Navigation, X } from "lucide-react";
import { KeyboardAwareDrawer, KeyboardAwareDrawerContent } from "@components/KeyboardAwareDrawer";
import { DrawerTitle, DrawerTrigger } from "@shadcn/ui/drawer";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLazyLoadQuery } from "react-relay";
import { graphql } from "relay-runtime";
import { getDistanceGCS } from "@utils/coords";
import { CityPicker } from "./CityPicker";
import { HubMapQuery } from "./__generated__/HubMapQuery.graphql";
import { HubTrack, HubTrackActionRenderer } from "./HubTrack";
import { MapView, MapViewHandle } from "./MapView";
import { filterHubs, getHubMapInitialSelection, HubFilterService, sortHubsByDistance } from "./hubFilters";

type HubMapProps = {
	trigger?: React.ReactNode;
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
	currentHubMetaName?: string | null;
	filterService?: HubFilterService | null;
	/** Restricts the map to these hubs (e.g. the hubs a One Delivery warehouse delivers to). */
	hubIds?: readonly string[];
	renderAction: HubTrackActionRenderer;
};

type Coordinates = { latitude: number; longitude: number };

type GeoState = { type: "pending" } | { type: "denied" } | { type: "granted"; location: Coordinates };

type HubCityCandidate = {
	meta?: {
		city?: string | null;
		lat?: number | null;
		lng?: number | null;
	} | null;
};

const hasHubCoordinates = (hub: HubCityCandidate): hub is { meta: { city?: string | null; lat: number; lng: number } } =>
	typeof hub.meta?.lat === "number" && typeof hub.meta?.lng === "number";

const getAutoSelectedCity = (location: Coordinates, hubs: HubCityCandidate[]) => {
	const calcDistance = getDistanceGCS(location.latitude, location.longitude);
	let closestCity: string | null = null;
	let closestDistance = Number.POSITIVE_INFINITY;

	for (const hub of hubs) {
		if (!hasHubCoordinates(hub)) continue;
		const city = hub.meta.city?.trim();
		if (!city) continue;

		const distance = calcDistance(hub.meta.lat, hub.meta.lng);
		if (distance < closestDistance) {
			closestDistance = distance;
			closestCity = city;
		}
	}

	return closestCity;
};

const query = graphql`
	query HubMapQuery @relay_test_operation {
		hubs(filter: { unclaimed: false }) {
			nodes {
				id
				status
				meta @required(action: THROW) {
					id
					city
					country
					postalCode
					name
					services
					street
					lat
					lng
				}
				sncProducts(filter: { active: true }) {
					totalCount
				}
			}
		}
	}
`;

export const HubMap = (props: HubMapProps) => {
	const { trigger, open, onOpenChange, filterService, currentHubMetaName, hubIds, ...hubTrackProps } = props;
	const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
	const isControlled = open !== undefined;
	const isOpen = open ?? uncontrolledOpen;
	const setIsOpen = (nextOpen: boolean) => {
		if (!isControlled) {
			setUncontrolledOpen(nextOpen);
		}

		onOpenChange?.(nextOpen);
	};

	const [selectedCity, setSelectedCity] = useState<string | null>(null);
	const [selectedHubId, setSelectedHubId] = useState<string | undefined>();
	const [geo, setGeo] = useState<GeoState>({ type: "pending" });
	const mapViewRef = useRef<MapViewHandle>(null);
	useBackButtonHijack(isOpen, () => setIsOpen(false));
	const i18n = useI18nContext().LL["new-ui"];

	const currentLocation = geo.type === "granted" ? geo.location : null;
	const locationStatus = geo.type;

	const data = useLazyLoadQuery<HubMapQuery>(query, {});

	const serviceFilteredHubs = useMemo(() => {
		const hubs = hubIds ? data.hubs.nodes.filter((hub) => hubIds.includes(hub.id)) : data.hubs.nodes;
		return filterHubs(hubs, { filterService });
	}, [data.hubs.nodes, filterService, hubIds]);

	const visibleHubs = useMemo(() => {
		return sortHubsByDistance(filterHubs(serviceFilteredHubs, { selectedCity }), currentLocation);
	}, [currentLocation, selectedCity, serviceFilteredHubs]);

	const initialSelection = useMemo(
		() => getHubMapInitialSelection(serviceFilteredHubs, currentHubMetaName),
		[currentHubMetaName, serviceFilteredHubs]
	);
	const cities = useMemo(() => {
		const cityMap = new Map<string, { id: string }>();

		for (const hub of serviceFilteredHubs) {
			const city = hub.meta.city?.trim();
			if (!city) {
				continue;
			}

			cityMap.set(city, cityMap.get(city) ?? { id: hub.id });
		}

		const sortedCities = Array.from(cityMap.entries())
			.map(([name, value]) => ({
				id: value.id,
				name,
			}))
			.sort((a, b) => a.name.localeCompare(b.name));

		if (!selectedCity) {
			return sortedCities;
		}

		const selectedIndex = sortedCities.findIndex((city) => city.name === selectedCity);
		if (selectedIndex <= 0) {
			return sortedCities;
		}

		const [selected] = sortedCities.splice(selectedIndex, 1);
		if (selected) {
			return [selected, ...sortedCities];
		} else {
			return sortedCities;
		}
	}, [serviceFilteredHubs, selectedCity]);

	const syncSelectedCityFromLocation = useCallback(
		(location: Coordinates) => {
			if (selectedCity) {
				return;
			}
			const autoSelectedCity = getAutoSelectedCity(location, serviceFilteredHubs);
			if (!autoSelectedCity || !cities.some((city) => city.name === autoSelectedCity) || autoSelectedCity === selectedCity) {
				return;
			}

			setSelectedCity(autoSelectedCity);
			setSelectedHubId(undefined);
		},
		[cities, selectedCity, serviceFilteredHubs]
	);

	useEffect(() => {
		if (!isOpen) {
			setGeo({ type: "pending" });
			return;
		}

		if (!navigator.geolocation) {
			setGeo({ type: "denied" });
			return;
		}

		navigator.geolocation.getCurrentPosition(
			({ coords }) => {
				setGeo({ type: "granted", location: { latitude: coords.latitude, longitude: coords.longitude } });
				syncSelectedCityFromLocation({ latitude: coords.latitude, longitude: coords.longitude });
			},
			() => {
				setGeo({ type: "denied" });
			},
			{ enableHighAccuracy: true, timeout: 10000 }
		);
	}, [isOpen, setGeo, syncSelectedCityFromLocation]);

	useEffect(() => {
		if (!isOpen || !currentHubMetaName) {
			return;
		}

		if (!initialSelection) {
			setSelectedCity(null);
			setSelectedHubId(undefined);
			return;
		}

		setSelectedCity(initialSelection.city);
		setSelectedHubId(initialSelection.hubId);
	}, [currentHubMetaName, initialSelection, isOpen]);

	useEffect(() => {
		if (!cities.length) {
			setSelectedCity(null);
			return;
		}

		setSelectedCity((currentCity) => {
			if (currentCity && cities.some((city) => city?.name === currentCity)) {
				return currentCity;
			}

			return null;
		});
	}, [cities]);

	const handleCitySelect = useCallback(
		(city: string) => {
			setSelectedCity(city);
			const visHubs = sortHubsByDistance(filterHubs(serviceFilteredHubs, { selectedCity: city }), currentLocation);
			if (visHubs[0]) {
				setSelectedHubId(visHubs[0].id);
			}
		},
		[serviceFilteredHubs, currentLocation]
	);

	return (
		<KeyboardAwareDrawer shouldScaleBackground dismissible={false} open={isOpen} onOpenChange={setIsOpen}>
			{trigger ? <DrawerTrigger className="text-left w-full">{trigger}</DrawerTrigger> : null}
			<KeyboardAwareDrawerContent
				hasHandle={false}
				className="overflow-hidden rounded-none border-0"
				style={{ top: 0, bottom: 0, height: "100dvh", maxHeight: "100dvh", marginTop: 0, borderRadius: 0 }}
			>
				<VisuallyHidden>
					<DrawerTitle>{i18n.SELECT_A_HUB()}</DrawerTitle>
				</VisuallyHidden>
				<div
					className="absolute inset-0 -z-[1]"
					onDrag={(e) => {
						e.preventDefault();
						e.stopPropagation();
					}}
					onPointerDown={(e) => {
						e.preventDefault();
						e.stopPropagation();
					}}
					onPointerMove={(e) => {
						e.preventDefault();
						e.stopPropagation();
					}}
				>
					{import.meta.env.VITE_MAPBOX_TOKEN ? (
						<MapView
							ref={mapViewRef}
							hubs={visibleHubs}
							selectedHubId={selectedHubId}
							setSelectedHubId={setSelectedHubId}
							locationStatus={locationStatus}
							currentLocation={currentLocation}
							onCurrentLocationChange={(location) => {
								setGeo({ type: "granted", location });
								syncSelectedCityFromLocation(location);
							}}
						/>
					) : (
						<img src="/mock/hub-map.png" alt={i18n.HUB_MAP_ALT()} className="w-full h-full object-cover absolute inset-0 -z-[1]" />
					)}
				</div>

				{!currentLocation && !selectedCity ? (
					<>
						<div className="absolute inset-0 z-[1] bg-black/70" />
						<div className="absolute inset-x-0 top-24 z-10 px-6">
							<div className="mx-auto flex max-w-xs flex-col items-center gap-6 text-center text-white">
								<img src="/consumer-home-assets/swirly-arrow.svg" alt="" className="relative -left-20 h-[106px] w-20" />
								<p className="font-breit text-2xl leading-none">{i18n.SELECT_CITY_TO_START()}</p>
							</div>
						</div>
					</>
				) : null}

				<div className="absolute inset-x-0 top-6 z-10 px-6" data-vaul-no-drag="">
					<div className="flex items-start gap-3">
						<div className="min-w-0 flex-1">
							<CityPicker selectedCity={selectedCity} cities={cities} onSelectCity={handleCitySelect} />
						</div>
						<Button
							size="icon"
							variant="outline"
							className="ml-auto size-10 bg-background/95 shadow-xl backdrop-blur"
							onClick={() => setIsOpen(false)}
						>
							<X className="size-4" strokeWidth={1.5} />
						</Button>
					</div>
				</div>

				{currentLocation || selectedCity ? (
					<div
						className="absolute inset-x-0 z-10 flex flex-col gap-4"
						style={{ bottom: "calc(1rem + env(safe-area-inset-bottom, 0px))" }}
						data-vaul-no-drag=""
					>
						{currentLocation ? (
							<div className="flex justify-end px-4">
								<Button
									type="button"
									variant="outline"
									size="icon"
									className="size-10 bg-background shadow-xl"
									onClick={() => mapViewRef.current?.centerCurrentLocation()}
									aria-label={i18n.CENTER_MAP_CURRENT_LOCATION()}
								>
									<Navigation className="size-4" strokeWidth={1.5} />
								</Button>
							</div>
						) : null}
						<HubTrack
							{...hubTrackProps}
							hubs={visibleHubs}
							isOpen={isOpen}
							close={() => setIsOpen(false)}
							selectedHubId={selectedHubId}
							setSelectedHubId={setSelectedHubId}
						/>
					</div>
				) : null}
			</KeyboardAwareDrawerContent>
		</KeyboardAwareDrawer>
	);
};
